<template>
  <div class="pa-6" style="min-width: 282px; background-color: var(--v-background-darken1); border-radius: 4px">
    <div v-if="stemdoer.stemdoId" class="image-container mb-6">
      <SAvatarStemdoer :stemdoId="stemdoer.stemdoId" size="75px" />
    </div>
    <div class="d-flex flex-column align-items-center mb-8">
      <span class="text-body-1-bold text--text text--base mb-2"> {{ stemdoer.name + ' ' + stemdoer.surname }} </span>
      <span class="text-body-1-bold text--text text--base"> {{ stemdoer.position }} </span>
    </div>

    <div class="d-flex flex-column">
      <div class="d-flex flex-column mb-4">
        <span class="text-body-2-bold text--text text--lighten-1 mb-2" v-text="$t('stemdoerDetail.email')"> </span>
        <span class="text-body-1 text--text text--base"> {{ stemdoer.email }} </span>
      </div>
      <div class="d-flex flex-column mb-4">
        <span class="text-body-2-bold text--text text--lighten-1 mb-2" v-text="$t('stemdoerDetail.employeeId')"> </span>
        <span class="text-body-1 text--text text--base"> {{ stemdoer.stemdoId }} </span>
      </div>
      <div class="d-flex flex-column mb-4">
        <span class="text-body-2-bold text--text text--lighten-1 mb-2" v-text="$t('stemdoerDetail.hireDate')"> </span>
        <span class="text-body-1 text--text text--base"> {{ $d(new Date(stemdoer.hireDate), 'twoDigit') }} </span>
      </div>
      <div v-if="lastCohortDate" class="d-flex flex-column mb-4">
        <span class="text-body-2-bold text--text text--lighten-1 mb-2"> {{ $t('stemdoerDetail.bootcampEndDate') }} </span>
        <span class="text-body-1 text--text text--base"> {{ $d(new Date(lastCohortDate), 'twoDigit') }} </span>
      </div>
      <div v-if="stemdoer.office && stemdoer.office !== ''" class="d-flex flex-column mb-4">
        <span class="text-body-2-bold text--text text--lighten-1 mb-2" v-text="$t('stemdoerDetail.office')"> </span>
        <span class="text-body-1 text--text text--base"> {{ stemdoer.office }} </span>
      </div>
      <div v-if="stemdoer.languages && stemdoer.languages.length > 0" class="d-flex flex-column mb-4">
        <span class="text-body-2-bold text--text text--lighten-1 mb-2" v-text="$t('stemdoerDetail.languages')"> </span>
        <div v-for="language in getLanguageBadges(stemdoer.languages)" :key="language.name" class="d-flex flex-column mb-2">
          <span class="text-body-1 text--text text--base"> {{ language.name }} {{ language.level }} </span>
        </div>
      </div>
      <div v-if="stemdoer.mbti && stemdoer.mbti != ''" class="d-flex flex-column mb-4">
        <span class="text-body-2-bold text--text text--lighten-1 mb-2" v-text="$t('stemdoerDetail.mbti')"> </span>
        <span class="text-body-1 text--text text--base"> {{ stemdoer.mbti }} </span>
      </div>
      <div v-if="platformSeniority != null" class="d-flex flex-column">
        <div class="d-flex align-items-center mb-2">
          <span class="text-body-2-bold text--text text--lighten-1" v-text="$t('stemdoerDetail.seniority')"></span>
          <div class="d-flex ml-2" style="cursor: pointer" @click="onEditSeniorityClick()">
            <img width="20px" :src="'/content/svgs/edit.svg'" />
          </div>
        </div>
        <span class="text-body-1 text--text text--base"> {{ platformSeniority }} </span>
      </div>
      <EditSeniorityModal
        v-if="showSeniorityModal"
        :isOpen="showSeniorityModal"
        :stemdoerId="stemdoer.id"
        :currentSeniority="selectedSeniority"
        :currentSeniorityLabel="platformSeniority"
        :onCloseModal="onCloseSeniorityModal"
        @seniority-updated="onSeniorityUpdated"
      />
      <SPopUp class="popUp" :isOpen="popUpData.isOpen" :sText="popUpData.message" :isValid="popUpData.isValid" />
    </div>
  </div>
</template>

<script lang="ts">
import { Stemdoer } from '@/shared/model/stemdoer.model';
import { computed, defineComponent, ref, watch } from 'vue';
import { useI18N } from '@/plugins/i18n';
import SAvatarStemdoer from '@/components/stemdo-components/s-avatar-stemdoer/SAvatarStemdoer.vue';
import EditSeniorityModal from './editSeniorityModal/EditSeniorityModal.vue';
import SPopUp from '@/components/stemdo-components/s-pop-up/SPopUp.vue';

export default defineComponent({
  props: {
    stemdoer: {
      type: Object as () => Stemdoer,
      required: true
    }
  },
  components: {
    SAvatarStemdoer,
    EditSeniorityModal,
    SPopUp
  },
  setup(props) {
    const i18n = useI18N();
    const showSeniorityModal = ref(false);
    const selectedSeniority = ref(props.stemdoer.seniority);
    const popUpData = ref({
      isOpen: false,
      message: '',
      isValid: true
    });

    // Sincroniza selectedSeniority si el prop cambia (por ejemplo, si el stemdoer cambia dinámicamente)
    watch(
      () => props.stemdoer.seniority,
      newVal => {
        selectedSeniority.value = newVal;
      }
    );

    const onEditSeniorityClick = () => {
      showSeniorityModal.value = true;
    };
    const onCloseSeniorityModal = () => {
      showSeniorityModal.value = false;
    };
    const showPopUp = (message: string, isValid = true) => {
      popUpData.value = {
        isOpen: true,
        message,
        isValid
      };
      setTimeout(() => {
        popUpData.value.isOpen = false;
      }, 2000);
    };
    const onSeniorityUpdated = (newSeniority: string) => {
      selectedSeniority.value = newSeniority;
      showPopUp(i18n.t('stemdoerDetail.seniorityModal.success').toString(), true);
    };

    const platformSeniority = computed(() => {
      switch (selectedSeniority.value) {
        case 'JUNIOR':
          return i18n.t('experienceLevel.explorer').toString();
        case 'MID_SENIOR':
          return i18n.t('experienceLevel.strategist').toString();
        case 'SENIOR':
          return i18n.t('experienceLevel.visionary').toString();
        default:
          return null;
      }
    });

    const getLanguageBadges = (languages: any[]) =>
      languages.map(language => {
        const translationKey = 'explore.stemdoer-detail.language.' + language.name.toLowerCase();
        const translatedName = i18n.t(translationKey) as string;
        return {
          name: translatedName !== translationKey ? translatedName : language.name,
          level: language.level
        };
      });

    const lastCohortDate = computed(() =>
      props.stemdoer.cohorts && Array.isArray(props.stemdoer.cohorts) && props.stemdoer.cohorts.length > 0
        ? props.stemdoer.cohorts.reduce(
            (latest: string | null, cohort: any) => (!latest || new Date(cohort.endDate) > new Date(latest) ? cohort.endDate : latest),
            null as string | null
          )
        : null
    );

    return {
      platformSeniority,
      lastCohortDate,
      showSeniorityModal,
      selectedSeniority,
      getLanguageBadges,
      onEditSeniorityClick,
      onCloseSeniorityModal,
      onSeniorityUpdated,
      popUpData
    };
  }
});
</script>

<style lang="scss" scoped>
.image-container {
  display: flex;
  justify-content: center;
  margin-top: -60px;
}

.image-container > * {
  z-index: 1; // Asegura que la imagen esté por encima del fondo gris
}

.popUp {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  margin: -32px 0px 0px 0px;
  width: fit-content;
  min-width: 18%;
}
</style>
